import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import refoulementService, { MotifRefoulement, RefoulementData, RefoulementResponse } from '@/services/refoulementService';
import weighingService from '@/services/weighing';
import historyService from '@/services/historyService';
import { RefoulementTicket } from '../tickets';
import { convertToRefoulementTicketData } from '@/utils/refoulementTicketConverter';
import { Pesée } from '@/types/weighing';
import { HistoryPesee } from '@/services/historyService';

/** Référence affichable (Pesée n'a que `reference`, HistoryPesee peut avoir `référence`) */
function peseeDisplayReference(p: Pesée | HistoryPesee | null | undefined): string | undefined {
  if (!p) return undefined;
  if (p.reference) return p.reference;
  if ('référence' in p) {
    const r = (p as HistoryPesee).référence;
    if (typeof r === 'string' && r) return r;
  }
  return undefined;
}

interface RefoulementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (refoulement: RefoulementResponse) => void;
  peseeId?: number | string;
  pesee?: Pesée | HistoryPesee | any; // Peut accepter différents formats de pesée
  peseeReference?: string;
  // Optionnel : données de pesée pré-chargées (pour compatibilité)
  peseeData?: {
    reference: string;
    grossWeight: number;
    tareWeight: number;
    netWeight: number;
    vehicle: string;
    driver: string;
    product: string;
    site: string;
    tenant: string;
    grossAt: string;
    tareAt: string;
    grossUser: string;
    tareUser: string;
  };
  autoLoadPesee?: boolean; // Si true, charge automatiquement les données de pesée
  showTicketAfterCreation?: boolean; // Si true, affiche le ticket après création
}

const RefoulementModal: React.FC<RefoulementModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  peseeId,
  pesee,
  peseeReference,
  peseeData,
  autoLoadPesee = true,
  showTicketAfterCreation = true
}) => {
  const [motifs, setMotifs] = useState<MotifRefoulement[]>([]);
  const [motifsGroupes, setMotifsGroupes] = useState<Record<string, MotifRefoulement[]>>({});
  const [selectedMotifs, setSelectedMotifs] = useState<string[]>([]);
  const [observations, setObservations] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredMotifs, setFilteredMotifs] = useState<MotifRefoulement[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [showTicket, setShowTicket] = useState(false);
  const [refoulementResponse, setRefoulementResponse] = useState<RefoulementResponse | null>(null);
  const [loadedPesee, setLoadedPesee] = useState<Pesée | HistoryPesee | null>(null);
  const [loadingPesee, setLoadingPesee] = useState(false);

  // Charger les motifs au montage du composant
  useEffect(() => {
    if (isOpen) {
      loadMotifs();
      if (autoLoadPesee && peseeId && !pesee) {
        loadPesee();
      } else if (pesee) {
        setLoadedPesee(pesee);
      }
    }
  }, [isOpen, peseeId, pesee, autoLoadPesee]);

  // Filtrer les motifs selon le terme de recherche
  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = motifs.filter(motif => {
        const searchLower = searchTerm.toLowerCase();
        return (
          (motif.nom && motif.nom.toLowerCase().includes(searchLower)) ||
          (motif.description && motif.description.toLowerCase().includes(searchLower)) ||
          (motif.categorie && motif.categorie.toLowerCase().includes(searchLower))
        );
      });
      setFilteredMotifs(filtered);
    } else {
      setFilteredMotifs(motifs);
    }
  }, [searchTerm, motifs]);

  // Charger les données de pesée si nécessaire
  const loadPesee = async () => {
    if (!peseeId) return;
    
    try {
      setLoadingPesee(true);
      setError('');
      
      // Essayer d'abord avec weighingService (format Pesée)
      try {
        const id = typeof peseeId === 'string' ? parseInt(peseeId) : peseeId;
        const response = await weighingService.getPesée(id);
        if (response.success && response.data) {
          setLoadedPesee(response.data);
          return;
        }
      } catch (err) {
        // Si ça échoue, essayer avec historyService (format HistoryPesee)
        try {
          const response = await historyService.getPeseeById(peseeId.toString());
          setLoadedPesee(response);
          return;
        } catch (err2) {
          console.error('Erreur lors du chargement de la pesée:', err2);
          setError('Impossible de charger les données de la pesée');
        }
      }
    } catch (err) {
      console.error('Erreur lors du chargement de la pesée:', err);
      setError('Impossible de charger les données de la pesée');
    } finally {
      setLoadingPesee(false);
    }
  };

  const loadMotifs = async () => {
    try {
      setLoading(true);
      setError('');
      const motifsData = await refoulementService.getMotifsRefoulement();
      
      const cleanedMotifs = motifsData.map((motif) => {
        return {
          ...motif,
          nom: motif.libelle || String(motif.id || ''),
          description: motif.description || 'Description non disponible',
          categorie: motif.categorie || 'Autres',
          criticite: motif.criticite || 'Non défini'
        };
      });
      
      setMotifs(cleanedMotifs);
      
      // Grouper les motifs par catégorie
      const groupes: Record<string, MotifRefoulement[]> = {};
      cleanedMotifs.forEach(motif => {
        const categorie = motif.categorie;
        if (!groupes[categorie]) {
          groupes[categorie] = [];
        }
        groupes[categorie].push(motif);
      });
      setMotifsGroupes(groupes);
    } catch (err) {
      console.error('Erreur lors du chargement des motifs:', err);
      setError('Erreur lors du chargement des motifs de refoulement');
    } finally {
      setLoading(false);
    }
  };

  const handleMotifToggle = (motifId: string) => {
    setSelectedMotifs(prev => {
      if (prev.includes(motifId)) {
        return prev.filter(id => id !== motifId);
      } else {
        return [...prev, motifId];
      }
    });
    setValidationErrors([]);
  };

  const handleSelectAllInCategory = (categorie: string) => {
    const motifsInCategory = motifsGroupes[categorie] || [];
    const motifIds = motifsInCategory.map(motif => motif.id);
    
    setSelectedMotifs(prev => {
      const allSelected = motifIds.every(id => prev.includes(id));
      if (allSelected) {
        return prev.filter(id => !motifIds.includes(id));
      } else {
        const newSelection = [...prev];
        motifIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      }
    });
  };

  const handleClearAll = () => {
    setSelectedMotifs([]);
    setObservations('');
    setSearchTerm('');
    setValidationErrors([]);
  };

  const toggleCategory = (categorie: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categorie]: !prev[categorie]
    }));
  };

  // Obtenir l'ID de pesée réel
  const getPeseeId = (): number => {
    if (peseeId) {
      return typeof peseeId === 'string' ? parseInt(peseeId) : peseeId;
    }
    if (pesee?.id) {
      return typeof pesee.id === 'string' ? parseInt(pesee.id) : pesee.id;
    }
    if (loadedPesee?.id) {
      return typeof loadedPesee.id === 'string' ? parseInt(loadedPesee.id) : loadedPesee.id;
    }
    throw new Error('ID de pesée non disponible');
  };

  // Obtenir la référence de pesée
  const getPeseeReference = (): string => {
    return (
      peseeReference ||
      peseeDisplayReference(pesee) ||
      peseeDisplayReference(loadedPesee) ||
      `#${getPeseeId()}`
    );
  };

  // Convertir les données de pesée en format compatible
  const getPeseeForTicket = () => {
    const currentPesee = pesee || loadedPesee;
    if (!currentPesee) {
      // Utiliser peseeData si disponible
      if (peseeData) {
        return {
          reference: peseeData.reference,
          code: peseeData.reference,
          poids_brut: peseeData.grossWeight,
          poids_tare: peseeData.tareWeight,
          poids_net: peseeData.netWeight,
          camion: peseeData.vehicle,
          chauffeur: peseeData.driver,
          produit: peseeData.product,
          pont_bascule: peseeData.site,
          heure_entree: peseeData.grossAt,
          heure_sortie: peseeData.tareAt,
          agent_premiere_pesee: peseeData.grossUser,
          agent_deuxieme_pesee: peseeData.tareUser
        };
      }
      return null;
    }
    return currentPesee;
  };

  const handleConfirm = async () => {
    // Validation
    const errors: string[] = [];
    
    if (selectedMotifs.length === 0) {
      errors.push('Au moins un motif doit être sélectionné');
    }
    
    if (observations.length > 1000) {
      errors.push('Les observations ne peuvent pas dépasser 1000 caractères');
    }

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      setLoading(true);
      setError('');
      setValidationErrors([]);
      
      const id = getPeseeId();
      const refoulementData: RefoulementData = {
        pesee_id: id,
        motifs: selectedMotifs,
        observations: observations.trim()
      };

      // Créer le refoulement via l'API
      const response = await refoulementService.creerRefoulement(refoulementData);
      
      // Sauvegarder la réponse pour le ticket
      setRefoulementResponse(response);
      
      // Afficher le ticket si demandé (AVANT d'appeler onSuccess pour éviter que le parent ferme le modal)
      if (showTicketAfterCreation) {
        setShowTicket(true);
      }
      
      // Appeler la callback parent (après avoir affiché le ticket)
      if (onSuccess) {
        onSuccess(response);
      }
      
      // Si on n'affiche pas le ticket, fermer le modal
      if (!showTicketAfterCreation) {
        handleClose();
      }
      
    } catch (err: any) {
      console.error('Erreur lors de la confirmation:', err);
      setError(err.message || 'Erreur lors de la création du refoulement');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowTicket(false);
    setRefoulementResponse(null);
    handleClearAll();
    setLoadedPesee(null);
    onClose();
  };

  const getMotifsGroupesToDisplay = () => {
    if (searchTerm.trim()) {
      const groupes: Record<string, MotifRefoulement[]> = {};
      filteredMotifs.forEach(motif => {
        const categorie = motif.categorie || 'Autres';
        if (!groupes[categorie]) {
          groupes[categorie] = [];
        }
        groupes[categorie].push(motif);
      });
      return groupes;
    }
    return motifsGroupes;
  };

  const getCriticiteColor = (criticite: string | undefined) => {
    if (!criticite) return 'text-gray-600 bg-gray-100';
    
    switch (criticite.toLowerCase()) {
      case 'critique': return 'text-red-600 bg-red-100';
      case 'haute': return 'text-orange-600 bg-orange-100';
      case 'moyenne': return 'text-yellow-600 bg-yellow-100';
      case 'faible': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const handleCloseTicket = () => {
    handleClose();
  };

  if (!isOpen) return null;

  // Afficher le ticket de refoulement si disponible
  if (showTicket && refoulementResponse) {
    const peseeForTicket = getPeseeForTicket();
    
    if (peseeForTicket) {
      const ticketData = convertToRefoulementTicketData(refoulementResponse, peseeForTicket);
      
      return (
        <RefoulementTicket
          ticketData={ticketData}
          onClose={handleCloseTicket}
          isModal={true}
        />
      );
    }
  }

  // Gestion d'erreur pour éviter les crashes
  try {
    return (
      <div className="fixed inset-0 z-[10000] overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-2 sm:p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={handleClose} />
        
        <div 
          className="relative bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] sm:max-h-[85vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Refoulement de pesée
                </h3>
                <p className="text-sm text-gray-600">
                  Référence: {getPeseeReference()}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={loading || loadingPesee}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0">
            {loadingPesee && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                <p className="text-sm text-blue-600">Chargement des données de la pesée...</p>
              </div>
            )}

            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {validationErrors.length > 0 && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <h4 className="text-sm font-medium text-red-800 mb-2">Erreurs de validation:</h4>
                <ul className="text-sm text-red-600 list-disc list-inside">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recherche */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rechercher un motif
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Tapez pour rechercher un motif..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Motifs sélectionnés */}
            {selectedMotifs.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-700">
                    Motifs sélectionnés ({selectedMotifs.length})
                  </h4>
                  <button
                    onClick={handleClearAll}
                    className="text-sm text-red-600 hover:text-red-800 flex items-center"
                  >
                    <TrashIcon className="h-4 w-4 mr-1" />
                    Tout effacer
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedMotifs.map((motifId: string) => {
                    const motif = motifs.find((m: MotifRefoulement) => m.id === motifId);
                    return motif ? (
                      <span
                        key={motifId}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-100 text-red-800"
                      >
                        {motif.nom}
                        <button
                          onClick={() => handleMotifToggle(motifId)}
                          className="ml-2 text-red-600 hover:text-red-800 transition-colors"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </span>
                    ) : (
                      <span
                        key={motifId}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-600"
                      >
                        Motif ID: {motifId}
                        <button
                          onClick={() => handleMotifToggle(motifId)}
                          className="ml-2 text-gray-600 hover:text-gray-800 transition-colors"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Liste des motifs avec accordéons */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-4">
                Sélectionner les motifs de refoulement
              </h4>
              
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-600">Chargement des motifs...</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {Object.entries(getMotifsGroupesToDisplay()).map(([categorie, motifsInCategory]) => {
                    const isExpanded = expandedCategories[categorie] || false;
                    const selectedInCategory = motifsInCategory.filter(motif => selectedMotifs.includes(motif.id)).length;
                    const totalInCategory = motifsInCategory.length;
                    
                    return (
                      <div key={categorie} className="border border-gray-200 rounded-lg overflow-hidden">
                        {/* En-tête de l'accordéon */}
                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                          <div className="flex items-center justify-between">
                            <button
                              onClick={() => toggleCategory(categorie)}
                              className="flex items-center flex-1 text-left hover:bg-gray-100 p-2 -m-2 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
                            >
                              {isExpanded ? (
                                <ChevronDownIcon className="h-5 w-5 text-gray-500 mr-2" />
                              ) : (
                                <ChevronRightIcon className="h-5 w-5 text-gray-500 mr-2" />
                              )}
                              <h5 className="font-medium text-gray-900">
                                {categorie}
                                {selectedInCategory > 0 && (
                                  <span className="ml-2 text-sm text-red-600 font-normal">
                                    ({selectedInCategory}/{totalInCategory} sélectionnés)
                                  </span>
                                )}
                              </h5>
                            </button>
                            <div className="flex items-center space-x-2 ml-4">
                              {selectedInCategory > 0 && (
                                <span className="text-xs text-gray-500">
                                  {selectedInCategory === totalInCategory ? 'Tous sélectionnés' : `${selectedInCategory} sur ${totalInCategory}`}
                                </span>
                              )}
                              <button
                                onClick={() => handleSelectAllInCategory(categorie)}
                                className="text-sm text-red-600 hover:text-red-800 flex items-center px-2 py-1 rounded hover:bg-red-50 transition-colors"
                              >
                                <PlusIcon className="h-4 w-4 mr-1" />
                                {selectedInCategory === totalInCategory ? 'Tout désélectionner' : 'Tout sélectionner'}
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Contenu de l'accordéon */}
                        {isExpanded && (
                          <div className="p-4 space-y-3 bg-white">
                            {motifsInCategory.map(motif => (
                              <label
                                key={motif.id}
                                className="flex items-start space-x-3 cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedMotifs.includes(motif.id)}
                                  onChange={() => handleMotifToggle(motif.id)}
                                  className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <p className="text-sm font-medium text-gray-900">
                                      {motif.nom || 'Nom non disponible'}
                                    </p>
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCriticiteColor(motif.criticite)}`}>
                                      {motif.criticite || 'Non défini'}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-600 mt-1">
                                    {motif.description || 'Description non disponible'}
                                  </p>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Observations */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observations détaillées
                <span className="text-gray-500 font-normal ml-1">
                  (optionnel - {observations.length}/1000 caractères)
                </span>
              </label>
              <textarea
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                rows={4}
                placeholder="Décrivez en détail les raisons du refoulement..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                maxLength={1000}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-4 sm:p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
              disabled={loading || loadingPesee}
            >
              Annuler
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading || loadingPesee || selectedMotifs.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading || loadingPesee ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {loadingPesee ? 'Chargement...' : 'Confirmation...'}
                </>
              ) : (
                <>
                  <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                  Confirmer le refoulement
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
    );
  } catch (error) {
    console.error('Erreur dans RefoulementModal:', error);
    return (
      <div className="fixed inset-0 z-[10000] overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={handleClose} />
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <ExclamationTriangleIcon className="h-12 w-12 text-red-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Erreur dans le modal
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Une erreur s'est produite lors du chargement du modal de refoulement.
              </p>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
};

export default RefoulementModal;
